import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { LoadingPlaceholderContainer } from '@tyro/core/loading-placeholder';
import { AddIcon } from '@tyro/icons';
import { useTyroneConversationsList } from '../../../../api/tyrone/list';
import { getRelativeDateFormat } from './relative-date-format';

interface ConversationListProps {
  onSelectConversation: (
    conversationId: string,
    initialMessage: string,
  ) => void;
  onNewConversation: () => void;
}

export function ConversationList({
  onSelectConversation,
  onNewConversation,
}: ConversationListProps) {
  const { customShadows } = useTheme();
  const { data: conversations, isLoading } = useTyroneConversationsList();

  return (
    <LoadingPlaceholderContainer isLoading={isLoading}>
      <List sx={{ flex: 1, overflowY: 'auto', px: 1, pt: 1 }}>
        <Box sx={{ pt: 0.5, pb: 1.5 }}>
          <Button
            variant="soft"
            fullWidth
            startIcon={<AddIcon />}
            onClick={onNewConversation}
          >
            New conversation
          </Button>
        </Box>
        {conversations?.length === 0 ? (
          <Stack
            sx={{
              alignItems: 'center',
              justifyContent: 'center',
              p: 4,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              No conversations yet
            </Typography>
          </Stack>
        ) : (
          conversations?.map((conversation) => (
            <ListItemButton
              key={conversation.id}
              onClick={() =>
                onSelectConversation(
                  conversation.id,
                  conversation.initialMessage,
                )
              }
              sx={{
                borderRadius: 2,
                mb: 1,
                boxShadow: customShadows.card,
                backgroundColor: 'background.paper',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <ListItemText
                primary={conversation.initialMessage}
                secondary={getRelativeDateFormat(conversation.updatedOn)}
                slotProps={{
                  primary: {
                    noWrap: true,
                    variant: 'body2',
                    fontWeight: 500,
                  },
                  secondary: {
                    variant: 'caption',
                    color: 'text.secondary',
                  },
                }}
              />
            </ListItemButton>
          ))
        )}
      </List>
    </LoadingPlaceholderContainer>
  );
}
